import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NEO_DIALOG_DATA, NeoDialogModule, NeoDialogRef } from '@neogov/pattern-library/neo-dialog';
import { ButtonComponent } from '@presentation/components';

export interface NotificationDetailModalData {
  id: number;
  title: string;
  body: string;
  typeLabel: string;
  /** Neutral entity reference — a case number or an investigation number depending on context. */
  entityNumber: string | null;
  entityId: number | null;
  createdOn: Date;
  link: string | null;
  /** aut-id prefix: `''` for Cases, `'investigation-'` for Investigations. */
  autIdPrefix: string;
}

export type NotificationDetailModalResult = 'open-entity' | 'close';

@Component({
  selector: 'cm-notification-detail-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, NeoDialogModule, ButtonComponent],
  templateUrl: './notification-detail-modal.component.html',
  styleUrl: './notification-detail-modal.component.less',
})
export class NotificationDetailModalComponent {
  public readonly data = inject<NotificationDetailModalData>(NEO_DIALOG_DATA);
  private readonly _dialogRef =
    inject<NeoDialogRef<NotificationDetailModalComponent, NotificationDetailModalResult>>(NeoDialogRef);

  public readonly relativeReceived = this._formatRelative(this.data.createdOn);

  public onOpenEntity(): void {
    this._dialogRef.close('open-entity');
  }

  public onClose(): void {
    this._dialogRef.close('close');
  }

  private _formatRelative(date: Date): string {
    const diffMs = Date.now() - date.getTime();
    const minutes = Math.round(diffMs / 60_000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.round(hours / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }
}
